import React, { type FC, type PropsWithChildren } from 'react';

import { getUsernameCookie } from '@lucid/utils';
import { type ComponentImage, SiteNav, type SiteNavOptions } from '@lucid/components-react';
import { useMenuTopNavigation, useMenuProductSuite } from 'hooks/use-menu';
import useNavButtons, { placeholderButtons, placeholderLinks } from 'hooks/use-nav-buttons';
import type { I18n } from 'types/types';

export interface NavbarOptionalProps extends SiteNavOptions {
  /** Checks if the navbar is on a visio page. */
  isVisio?: boolean;
  /** The Drupal Node ID */
  nid?: string;
  /** The page type. */
  pageType?: string;
  /** Checks if we should hide the signup button. */
  shouldHideSignUpButton?: boolean;
}

export interface NavbarProps extends NavbarOptionalProps {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** The i18n translation data. */
  i18nData: I18n.DataEdges;
  /** The logo to be displayed in the Navbar. */
  logo: ComponentImage;
  /** The language currently set for the page context. */
  languageKey: I18n.AvailableLanguagesKey;
}

const Navbar: FC<PropsWithChildren<NavbarProps>> = ({
  buttonAriaLabel,
  entityTranslations = [],
  i18nData,
  isVisio = false,
  languageKey,
  logo,
  pageType,
  shouldHideSignUpButton,
  ...rest
}) => {
  const navbarMenu = useMenuTopNavigation(languageKey);
  const topNavMenu = useMenuProductSuite(languageKey);

  const excludeTopNavOn = [
    'ppc-diagram-landing-page',
    'competitor-ppc',
    'fifteen-og-ppc',
    'consideration-ppc',
    'enterprise',
    'twentyone-enterprise',
    'competitor',
    'consideration',
    'consideration-competitor',
    'discovery',
    'discovery-organic',
    'fifteen-og',
  ];
  const hasSuperNav = !excludeTopNavOn.includes(pageType || '');

  const username = getUsernameCookie();
  const initialNavButtons = useNavButtons(username, isVisio, pageType === 'discovery', shouldHideSignUpButton);

  return (
    <SiteNav
      {...rest}
      buttonAriaLabel={buttonAriaLabel}
      buttons={initialNavButtons.navButtons}
      entityTranslations={entityTranslations}
      hasSuperNav={hasSuperNav}
      i18nData={i18nData}
      items={navbarMenu}
      logo={logo}
      placeholderButtons={placeholderButtons}
      placeholderLinks={placeholderLinks}
      shouldHideSignUpButton={shouldHideSignUpButton}
      topButtons={initialNavButtons.superNavButtons || []}
      topItems={topNavMenu}
    />
  );
};

export default Navbar;
